import { useMemo } from 'react'
import useOrder from 'hooks/Order/useOrder'
import useOrderFlightName from 'luxPlus/hooks/useOrderFlightName'

interface OrderItemInfo {
  name?: string
  itemType?: string
}

function resolveOrderItemInfo(order: App.Order, itemId: string): OrderItemInfo {
  const tourItem = order.tourItems?.find(i => i.id === itemId)
  if (tourItem) return { name: tourItem.tour.title, itemType: 'tour' }

  const hotelItem = order.items?.find(i => i.id === itemId)
  if (hotelItem) return { name: hotelItem.offer.property?.name, itemType: 'hotel' }

  const bedbankItem = order.bedbankItems?.find(i => i.id === itemId)
  if (bedbankItem) return { name: bedbankItem.offer.property?.name, itemType: 'bedbank' }

  const cruiseItem = order.cruiseItems?.find(i => i.id === itemId)
  if (cruiseItem) return { name: cruiseItem.offerName, itemType: 'cruise' }

  const experienceItem = order.experienceItems?.find(i => i.id === itemId)
  if (experienceItem) return { name: experienceItem.title, itemType: 'experience' }

  const insuranceItem = order.insuranceItems?.find(i => i.id === itemId) ?? order.bookingProtectionItems?.find(i => i.id === itemId)
  if (insuranceItem) return { name: insuranceItem.productName, itemType: 'insurance' }

  const addonItem = order.addonItems?.find(i => i.id === itemId)
  if (addonItem) return { name: addonItem.name, itemType: 'experience' }

  const flightItem = order.flightItems?.find(i => i.id === itemId)
  // Flight name is resolved separately (see useOrderFlightName) so it can match the
  // full-airport-name route title shown on the My Escapes tile.
  if (flightItem) return { name: undefined, itemType: 'flight' }

  const carHireItem = order.carHireItems?.find(i => i.id === itemId)
  if (carHireItem) return { name: carHireItem.reservation?.vehicle.model, itemType: 'car_hire' }

  const tourExperienceItem = order.tourExperienceItems?.find(i => i.orderItemId === itemId)
  if (tourExperienceItem) return { name: tourExperienceItem.name, itemType: 'tour_experience' }

  return {}
}

interface UseOrderItemNameResult {
  name?: string
  itemType?: string
  fetching: boolean
}

function useOrderItemName(orderId: string | undefined, itemId: string): UseOrderItemNameResult {
  const [order, fetching] = useOrder(orderId)

  const info = useMemo(() => {
    if (!order) return {}
    return resolveOrderItemInfo(order, itemId)
  }, [order, itemId])

  const isFlight = info.itemType === 'flight'
  const { flightName, fetching: flightFetching } = useOrderFlightName(isFlight ? order : undefined)

  const name = isFlight ? flightName : info.name

  return { name, itemType: info.itemType, fetching: fetching || (isFlight && flightFetching) }
}

export default useOrderItemName
